'use client';

import React from 'react';

import PulseToggle from 'src/components/PulseUI/PulseToggle/PulseToggle';
import {
  FilterLabel,
  ClearFilterButton,
} from 'src/components/FilterSets/components';
import useFilterSets from 'src/hooks/filters/useFilterSets';
import { FilterType } from 'src/context/reducers/filterSetTypes';
import type { FilterValue } from 'src/context/reducers/filterSetReducer';

// Three states: 'true' = independent only, 'false' = not independent only,
// '' = All (no filter). Stored as a single-element `values` array on the
// standard FilterValue, so it serializes through the shared filter-set
// machinery. Mirrors the INPATIENT boolean filter — the backend coerces the
// first value string to a real bool.
type Selection = '' | 'true' | 'false';

// Matches neither segment so PulseToggle renders no active segment; paired with
// a wrapper that hides the bg-sky-600 slider to read as empty (All). PulseToggle
// has no native unselected state — it always slides its highlight under a side.
const TOGGLE_NONE = '__none__';

// Force a uniform segment weight. PulseToggle bolds the active segment and
// animates it (transition-all), which makes clearing look like the text resizes.
// The white-on-blue slider already signals the active side.
const TOGGLE_UNIFORM_WEIGHT = '[&_button>div]:!font-normal';

/**
 * Filter-set Independent/Not Independent/All filter. Matches the SiteOfCare/
 * PayerType layout: FilterLabel + the shared ClearFilterButton + the control.
 */
const Independent = ({
  location,
  smallLabel = false,
}: {
  location: string;
  smallLabel?: boolean;
}) => {
  const { filterSet, updateFilter } = useFilterSets(location);
  const filterValue = filterSet.independent;
  const selection = String(filterValue?.values?.[0]?.value ?? '') as Selection;
  const isActive = selection !== '';

  const handleChange = (value: string) => {
    const nextFilter: FilterValue = {
      type: FilterType.INDEPENDENT,
      is_excluded: false,
      is_all: false,
      values: [
        {
          value,
          label: value === 'true' ? 'Independent' : 'Not Independent',
        },
      ],
    };
    updateFilter('independent', nextFilter);
  };

  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="flex items-center justify-between">
        <FilterLabel smallLabel={smallLabel}>Independence</FilterLabel>
        <ClearFilterButton
          type="independent"
          isDisabled={!isActive}
          location={location}
        />
      </div>
      <div
        className={
          isActive
            ? `w-full ${TOGGLE_UNIFORM_WEIGHT}`
            : `w-full [&_.bg-sky-600]:hidden ${TOGGLE_UNIFORM_WEIGHT}`
        }
      >
        <PulseToggle<string>
          value={isActive ? selection : TOGGLE_NONE}
          onChange={handleChange}
          alwaysShowLabels
          className="w-full !border-neutral-300"
          aria-label="Independent or not independent"
          data-testid={`independent-toggle-${location}`}
          options={[
            { value: 'true', label: 'Independent' },
            { value: 'false', label: 'Not Independent' },
          ]}
        />
      </div>
    </div>
  );
};

export default Independent;
